import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Manuscript, ManuscriptStats, ManuscriptStatus } from '@/types';

export interface CreateManuscriptPayload {
  title: string;
  subtitle?: string;
  author: string;
  type: Manuscript['type'];
  genre?: string;
  perspective: Manuscript['perspective'];
  targetWordCount?: number;
  description?: string;
  status: ManuscriptStatus;
  tags?: string[];
  color?: string;
}

export interface UpdateManuscriptPayload extends Partial<CreateManuscriptPayload> {
  id: string;
  chapterCount?: number;
  wordCount?: number;
  coverImage?: string;
  editorFontSize?: number;
  editorLineHeight?: number;
  lastOpenedAt?: Date;
}

const defaultFontSize = 16;
const defaultLineHeight = 1.6;

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const randomSuffix = Math.random().toString(36).slice(2, 10);
  return `manuscript-${randomSuffix}`;
};

const cloneManuscripts = (collection: Manuscript[]): Manuscript[] => {
  return collection.map((item) => ({ ...item }));
};

export const useManuscriptsStore = defineStore('manuscripts', () => {
  const manuscripts = ref<Manuscript[]>([]);
  const activeManuscriptId = ref<string | null>(null);
  const isLoading = ref(false);
  const loadError = ref<string | null>(null);

  const activeManuscript = computed<Manuscript | null>(() => {
    if (!activeManuscriptId.value) {
      return null;
    }
    return manuscripts.value.find((item) => item.id === activeManuscriptId.value) ?? null;
  });

  const manuscriptCount = computed(() => manuscripts.value.length);

  const hasManuscript = computed(() => activeManuscript.value !== null);

  const recentManuscripts = computed(() => {
    const sorted = cloneManuscripts(manuscripts.value)
      .filter((item) => Boolean(item.lastOpenedAt))
      .sort((a, b) => {
        const aTime = a.lastOpenedAt ? a.lastOpenedAt.getTime() : a.updatedAt.getTime();
        const bTime = b.lastOpenedAt ? b.lastOpenedAt.getTime() : b.updatedAt.getTime();
        return bTime - aTime;
      });

    return sorted.slice(0, 3);
  });

  const currentManuscriptStats = computed<ManuscriptStats | null>(() => {
    if (!activeManuscript.value) {
      return null;
    }

    const manuscript = activeManuscript.value;
    const chapterCount = manuscript.chapterCount;
    const wordCount = manuscript.wordCount;

    return {
      chapterCount,
      wordCount,
      emptyChapters: 0,
      completedChapters: 0,
      averageChapterLength: chapterCount === 0 ? 0 : Math.round(wordCount / chapterCount),
      longestChapter: null,
      shortestChapter: null,
      progressPercent: manuscript.targetWordCount
        ? Math.min(100, Math.round((wordCount / manuscript.targetWordCount) * 100))
        : 0
    };
  });

  const createManuscript = async (input: CreateManuscriptPayload): Promise<Manuscript> => {
    isLoading.value = true;
    loadError.value = null;

    try {
      const now = new Date();
      const manuscript: Manuscript = {
        id: generateId(),
        title: input.title,
        subtitle: input.subtitle,
        author: input.author,
        type: input.type,
        genre: input.genre,
        perspective: input.perspective,
        storySummary: input.description ?? '',
        targetWordCount: input.targetWordCount,
        description: input.description,
        chapterCount: 0,
        wordCount: 0,
        lastChapterId: undefined,
        status: input.status,
        createdAt: now,
        updatedAt: now,
        lastOpenedAt: now,
        coverImage: undefined,
        editorFontSize: defaultFontSize,
        editorLineHeight: defaultLineHeight,
        tags: input.tags,
        color: input.color
      };

      manuscripts.value = [...manuscripts.value, manuscript];
      activeManuscriptId.value = manuscript.id;

      return manuscript;
    } catch (error) {
      console.error('Failed to create manuscript', { error, input });
      loadError.value = 'Unable to create manuscript.';
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const updateManuscript = async (payload: UpdateManuscriptPayload): Promise<Manuscript> => {
    const targetId = payload.id;
    const index = manuscripts.value.findIndex((item) => item.id === targetId);

    if (index === -1) {
      console.error('Attempted to update a manuscript that does not exist', { manuscriptId: targetId });
      throw new Error('Manuscript not found');
    }

    isLoading.value = true;
    loadError.value = null;

    try {
      const existing = manuscripts.value[index];
      const updated: Manuscript = {
        ...existing,
        ...payload,
        updatedAt: new Date()
      };

      if (payload.lastOpenedAt) {
        updated.lastOpenedAt = payload.lastOpenedAt;
      }

      manuscripts.value = manuscripts.value.map((item) => (item.id === targetId ? updated : item));

      if (activeManuscriptId.value === targetId) {
        activeManuscriptId.value = updated.id;
      }

      return updated;
    } catch (error) {
      console.error('Failed to update manuscript', { error, manuscriptId: targetId, payload });
      loadError.value = 'Unable to update manuscript.';
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const setActiveManuscript = async (manuscriptId: string): Promise<void> => {
    const target = manuscripts.value.find((item) => item.id === manuscriptId);
    if (!target) {
      console.error('Attempted to activate a manuscript that does not exist', { manuscriptId });
      throw new Error('Manuscript not found');
    }

    const now = new Date();
    const updated = { ...target, lastOpenedAt: now, updatedAt: now };

    manuscripts.value = manuscripts.value.map((item) => (item.id === manuscriptId ? updated : item));
    activeManuscriptId.value = manuscriptId;
  };

  const replaceManuscripts = (seed: Manuscript[]): void => {
    manuscripts.value = [...seed];
    activeManuscriptId.value = seed.length > 0 ? seed[0].id : null;
  };

  const updateStorySummary = (summary: string): void => {
    if (!activeManuscript.value) {
      return;
    }
    const updated = { ...activeManuscript.value, storySummary: summary, updatedAt: new Date() };
    manuscripts.value = manuscripts.value.map((item) => (item.id === updated.id ? updated : item));
  };

  const resetStore = (): void => {
    manuscripts.value = [];
    activeManuscriptId.value = null;
    isLoading.value = false;
    loadError.value = null;
  };

  return {
    manuscripts,
    activeManuscript,
    activeManuscriptId,
    manuscriptCount,
    hasManuscript,
    recentManuscripts,
    currentManuscriptStats,
    isLoading,
    loadError,
    createManuscript,
    updateManuscript,
    setActiveManuscript,
    replaceManuscripts,
    updateStorySummary,
    resetStore
  };
});

